/**
 * BranchingEngine — client-side conditional page navigation for SurveyJS forms (PADD-3742).
 *
 * Replicates the old AD Inform behaviour where selecting a particular answer can jump the
 * user straight to a target page (skipping intermediate pages) or complete the form, instead
 * of always advancing linearly.
 *
 * The engine is OPT-IN: it only does anything when it's constructed with a non-empty
 * `branchingRules` map. For a form with no branching, nextPage()/prevPage() fall through to
 * the exact same linear navigation SurveyJS would do on its own, so non-branching forms are
 * completely unaffected.
 *
 * branchingRules shape (built by the backend, keyed by SurveyJS question name):
 *   {
 *     "RADIO_inf_wtcInformControl_634_1948_21802": {
 *       options: { "31816": { targetPage: 2, terminateForm: false } },
 *       noAnswer: { targetPage: 3, terminateForm: false } | null,
 *       default:  { targetPage: 1, terminateForm: false } | null
 *     }
 *   }
 *
 * targetPage is a 0-indexed page number in survey.pages[]. The backend has already resolved
 * Inform section codes to page indices, so the engine never needs the section mapping.
 *
 * Back navigation is history-based: the engine records the exact path taken forward and
 * retraces it on prevPage(), rather than re-evaluating branching in reverse (which the old
 * AD also did via its TraveledQuestion history).
 */
export default class BranchingEngine {
    /**
     * @param {Model} survey - the SurveyJS model instance
     * @param {Object} branchingRules - map of question name → rule (see shape above)
     * @param {Object} [options]
     * @param {Function} [options.onValidateFail] - called when current-page validation blocks a forward move
     */
    constructor(survey, branchingRules, options = {}) {
        this.survey = survey;
        this.rules = branchingRules || {};
        this.onValidateFail = options.onValidateFail;
        // History of page indices visited on the way forward. Seeded with the starting page
        // so the first prevPage() has somewhere to return to.
        this.history = [survey ? (survey.currentPageNo || 0) : 0];
    }

    /** True when at least one branching rule exists — i.e. the engine can actually branch. */
    get isActive() {
        return this.rules && Object.keys(this.rules).length > 0;
    }

    /**
     * Advance to the next page, honouring branching rules on the current page's questions.
     *
     * Returns true if navigation happened (or the form was completed), false if blocked by
     * validation or already on the last page with no branch target.
     */
    nextPage() {
        const survey = this.survey;
        if (!survey) return false;

        // Validate the current page first — mirrors the host's per-page validation on NEXT.
        if (!survey.validateCurrentPage()) {
            if (this.onValidateFail) this.onValidateFail();
            return false;
        }

        const currentIndex = survey.currentPageNo;

        // No branching configured at all → pure linear navigation (identical to before).
        if (!this.isActive) {
            return this._goLinear(currentIndex);
        }

        const currentPage = survey.pages[currentIndex];
        if (!currentPage) return false;

        const branchTargets = [];
        let terminate = false;

        // Inspect every question on the current page for a matching branching rule.
        const questions = currentPage.questions || [];
        questions.forEach((q) => {
            const rule = this.rules[q.name];
            if (!rule) return;

            const value = q.value;
            const hasAnswer = value !== undefined && value !== null
                && !(Array.isArray(value) && value.length === 0)
                && !(typeof value === "string" && value.length === 0);

            if (!hasAnswer) {
                // No answer → use the no-answer branch if one is configured.
                if (rule.noAnswer) {
                    if (rule.noAnswer.terminateForm) terminate = true;
                    else branchTargets.push(rule.noAnswer.targetPage);
                }
                return;
            }

            // An answer is present. Checkbox questions hold an array of selected option codes;
            // single-select questions hold a scalar. Check each selected value against the rule.
            const selected = Array.isArray(value) ? value : [value];
            let matchedOption = false;
            selected.forEach((sv) => {
                const target = rule.options ? rule.options[sv] : null;
                if (target) {
                    matchedOption = true;
                    if (target.terminateForm) terminate = true;
                    else branchTargets.push(target.targetPage);
                }
            });

            // Answer selected but no explicit option rule matched → fall back to the question's
            // default branch, if configured.
            if (!matchedOption && rule.default) {
                if (rule.default.terminateForm) terminate = true;
                else branchTargets.push(rule.default.targetPage);
            }
        });

        // Terminate wins over any page jump — selecting a "submit" option completes the form.
        if (terminate) {
            survey.doComplete();
            return true;
        }

        if (branchTargets.length > 0) {
            // Priority resolution: when several questions on the page branch to different pages,
            // take the NEAREST forward page (Math.min), matching old AD behaviour.
            const target = Math.min(...branchTargets);
            return this._goTo(target, currentIndex);
        }

        // Nothing branched → linear.
        return this._goLinear(currentIndex);
    }

    /**
     * Go back to the previously visited page by retracing the recorded history. Never
     * re-evaluates branching backwards.
     *
     * Returns true if it moved, false if already at the start of the history.
     */
    prevPage() {
        const survey = this.survey;
        if (!survey) return false;

        if (this.history.length > 1) {
            // Drop the current page and return to the one before it.
            this.history.pop();
            const previous = this.history[this.history.length - 1];
            survey.currentPageNo = previous;
            return true;
        }

        // No history to retrace — fall back to SurveyJS's own prevPage (first page = no-op).
        if (survey.isFirstPage) return false;
        survey.prevPage();
        return true;
    }

    // ─── internals ──────────────────────────────────────────────────────────

    /** Navigate to an explicit 0-indexed page, recording it in history. */
    _goTo(targetIndex, currentIndex) {
        if (targetIndex === currentIndex) return false;
        if (targetIndex < 0 || targetIndex >= this.survey.pages.length) {
            // Out-of-range target (defensive) → treat as linear.
            return this._goLinear(currentIndex);
        }
        this.survey.currentPageNo = targetIndex;
        this.history.push(targetIndex);
        return true;
    }

    /**
     * Advance to the immediate next page. V1 never skips pages on a linear step: a page is only
     * bypassed because an earlier answer jumped over it, not by any sibling-branch rule.
     */
    _goLinear(currentIndex) {
        if (this.survey.isLastPage) return false;

        const next = currentIndex + 1;
        this.survey.currentPageNo = next;
        this.history.push(next);
        return true;
    }
}
